// 필드 렌더(레인 F): 이끼 유적 필드 구역 1의 청크 환경 + 필드·균열·마을① 필드 문 표식.
// 청크 환경은 스테이지 지도 환경(env/stageEnv)의 부품을 그대로 쓴다: 판정 격자 → 거리장 등고선(stageEdge) → 바닥 윗면·절벽 단면(stageSurfaces 재질),
// 가장자리 트림·소품·랜드마크(지역1 세트피스 GLB·유적 키트), 풀·꽃 카드(fx). 청크마다 둘레 4칸을 덧댄 격자로 등고선을 만들고 청크 안 조각만 남겨
// 경계에서 이음매 없이 붙는다(같은 씨앗·같은 0.5m 격자라 겹친 곳의 등고선이 같다).
// 로드·해제: 25청크를 시작 때 뒤에서 한 청크씩 만들어 두고(장면 전환 밖), 게임의 활성 청크(view.field.activeChunks 3×3)만 보인다.
// 트림·소품·랜드마크·풀은 종류마다 인스턴싱 하나이고, 활성 청크가 바뀔 때만 그 청크들 인스턴스를 앞쪽으로 모은다(새 배열 없음, 복사만).
// 다른 플레이어(레인 M): view.field.net?.peers를 읽는 자리만 둔다(그림은 render/field/peers.ts — 레인 M).
import * as THREE from 'three/webgpu';
import type { QualityLevel } from '../../shared/config';
import { Cell, type MapDef, type MapGrid } from '../../shared/levels';
import { Scene, type GameView } from '../../shared/types';
import { FIELD_GRID_SPEC as G, CHUNK_COUNT } from '../../shared/field/constants';
import { FIELD_DOOR, fieldZone1, type ChunkTheme, type FieldChunkDef, type FieldDef } from '../../shared/field/layout';
import { FieldEventKind, RiftPhase } from '../../shared/field/view';
import { getR1StageMaps } from '../../shared/stageMaps';
import { buildField, contourOf, edgeProximity, resample, shoreDistance, type Chain } from '../env/stageEdge';
import { dataTexture, stageCliffMaterial, stageFloorMaterial, stageWaterMaterial, type ProximityTex } from '../env/stageSurfaces';
import { loadSet, loadSetAtlas, setMaterial, type SetAsset } from '../env/sets';
import { STYLE_MODULES, type SetName } from '../env/stageSets';
import { loadKit, loadTexture, track, type KitAsset } from '../env/kits';
import { kitMaterial, backdropMaterial } from '../env/surfaces';
import { cardField, flameField } from '../env/fx';
import type { Emitter } from '../env/layout';
import { moodOf } from '../env/mood';
import { mulberry32, valueNoise } from '../env/rng';
import { labelSprite } from '../env/label';
import { compose } from '../util';
import type { GameEvent } from '../../shared/events';
import type { ShotId } from '../cine';
import { GRIFT_COLOR, GriftFx } from './grift';

const CLIFF_DEPTH = 26;
/** 귀환석 결정·고리 불투명도: 멀리(far)에서는 눈에 띄게, 영웅이 가까이(near) 서면 전투 시야를 가리지 않게 */
export const STONE_CRYSTAL_OPACITY = { near: 0.16, far: 0.8 } as const;
export const STONE_RING_OPACITY = { near: 0.14, far: 0.5 } as const;
/** 영웅-귀환석 거리(m) → 0(가까움) .. 1(멂). 4m 안은 가장 투명, 10m 밖은 원래 밝기. 매 프레임 부르므로 할당 없음 */
export function stoneFade(dist: number): number {
  const t = Math.min(1, Math.max(0, (dist - 4) / 6));
  return t * t * (3 - 2 * t);
}
const WATER_Y = -1.0;
const PAD = 4;
const DENSITY: Record<QualityLevel, number> = { low: 0.5, mid: 0.75, high: 1 };
const FLOOR_TEX: Record<ChunkTheme, string> = { a: 'r1/tex/floor_moss_stone.webp', b: 'village/tex/plaza_cobble.webp', m: 'r1/tex/floor_dirt_arena.webp', f: 'r1/tex/floor_root_moss.webp' };
const STONE_CAP: readonly [number, number, number] = [0.42, 0.44, 0.32], WOOD_CAP: readonly [number, number, number] = [0.34, 0.25, 0.15], BARK_CAP: readonly [number, number, number] = [0.3, 0.22, 0.13];
const SET_CAP: Partial<Record<SetName, readonly [number, number, number]>> = { edge_palisade: WOOD_CAP, edge_root: BARK_CAP, tower: WOOD_CAP, bonfire: WOOD_CAP, root_knot: BARK_CAP, prop_crates: WOOD_CAP, prop_mushrooms: BARK_CAP };
const SCATTER: Record<ChunkTheme, readonly { kind: SetName; h: readonly [number, number] }[]> = {
  a: [{ kind: 'prop_rubble', h: [0.4, 0.7] }, { kind: 'prop_rock', h: [0.4, 0.7] }],
  b: [{ kind: 'prop_crates', h: [0.9, 1.2] }, { kind: 'prop_rubble', h: [0.4, 0.6] }],
  m: [{ kind: 'prop_crates', h: [0.9, 1.3] }, { kind: 'prop_rock', h: [0.4, 0.8] }],
  f: [{ kind: 'prop_mushrooms', h: [0.7, 1.0] }, { kind: 'prop_rock', h: [0.4, 0.8] }],
};

interface Inst { x: number; y: number; z: number; rot: number; sx: number; sy: number; sz: number; tint: number; cut: number }
/** 종류 하나의 인스턴싱 묶음: 청크별 원본(행렬·컷·색)과 활성 청크만 앞으로 모은 메시 */
class ChunkBatch {
  readonly mesh: THREE.InstancedMesh;
  private readonly cut: THREE.InstancedBufferAttribute;
  private readonly start = new Int32Array(CHUNK_COUNT + 1);
  private readonly mat: Float32Array;
  private readonly cutSrc: Float32Array;
  private readonly col: Float32Array;
  constructor(geo: THREE.BufferGeometry, material: THREE.Material, perChunk: readonly (readonly Inst[])[], shadow: boolean, name: string, withCut: boolean) {
    let n = 0;
    for (let c = 0; c < CHUNK_COUNT; c++) { this.start[c] = n; n += perChunk[c].length; }
    this.start[CHUNK_COUNT] = n;
    this.mat = new Float32Array(n * 16); this.cutSrc = new Float32Array(n * 4); this.col = new Float32Array(n * 3);
    const color = new THREE.Color();
    let i = 0;
    for (let c = 0; c < CHUNK_COUNT; c++) for (const p of perChunk[c]) {
      compose(this.mat, i * 16, p.x, p.y, p.z, p.rot, 0, p.sx, p.sy, p.sz);
      this.cutSrc[i * 4] = p.x; this.cutSrc[i * 4 + 1] = p.z; this.cutSrc[i * 4 + 2] = p.cut; this.cutSrc[i * 4 + 3] = p.sy;
      color.setHex(p.tint); this.col[i * 3] = color.r; this.col[i * 3 + 1] = color.g; this.col[i * 3 + 2] = color.b;
      i++;
    }
    const g = new THREE.BufferGeometry();
    if (geo.index) g.setIndex(geo.index);
    for (const a of ['position', 'normal', 'uv']) { const at = geo.getAttribute(a); if (at) g.setAttribute(a, at); }
    this.cut = new THREE.InstancedBufferAttribute(new Float32Array(Math.max(1, n) * 4), 4);
    this.cut.setUsage(THREE.DynamicDrawUsage);
    if (withCut) g.setAttribute('aCut', this.cut);
    this.mesh = new THREE.InstancedMesh(g, material, Math.max(1, n));
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.setColorAt(0, color.setRGB(1, 1, 1));
    this.mesh.instanceColor!.setUsage(THREE.DynamicDrawUsage);
    this.mesh.frustumCulled = false;
    this.mesh.castShadow = shadow; this.mesh.receiveShadow = true;
    this.mesh.name = name;
    this.mesh.count = 0;
  }
  /** 활성 청크 인스턴스를 앞쪽으로(복사만) */
  fill(active: Int8Array, count: number): void {
    const dm = this.mesh.instanceMatrix.array as Float32Array, dc = this.cut.array as Float32Array, dcol = this.mesh.instanceColor!.array as Float32Array;
    let m = 0;
    for (let k = 0; k < count; k++) {
      const c = active[k];
      if (c < 0) continue;
      const a = this.start[c], b = this.start[c + 1];
      dm.set(this.mat.subarray(a * 16, b * 16), m * 16);
      dc.set(this.cutSrc.subarray(a * 4, b * 4), m * 4);
      dcol.set(this.col.subarray(a * 3, b * 3), m * 3);
      m += b - a;
    }
    this.mesh.count = m;
    this.mesh.instanceMatrix.needsUpdate = true; this.cut.needsUpdate = true; this.mesh.instanceColor!.needsUpdate = true;
  }
}
/** 이미 만들어진 인스턴싱(풀 카드·불꽃)을 청크 범위로 나눠 같은 방식으로 모은다 */
class CardBatch {
  private readonly start = new Int32Array(CHUNK_COUNT + 1);
  private readonly mat: Float32Array;
  private readonly col: Float32Array | null;
  constructor(readonly mesh: THREE.InstancedMesh, counts: readonly number[]) {
    let n = 0;
    for (let c = 0; c < CHUNK_COUNT; c++) { this.start[c] = n; n += counts[c]; }
    this.start[CHUNK_COUNT] = n;
    this.mat = (mesh.instanceMatrix.array as Float32Array).slice();
    this.col = mesh.instanceColor ? (mesh.instanceColor.array as Float32Array).slice() : null;
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    mesh.count = 0;
  }
  fill(active: Int8Array, count: number): void {
    const dm = this.mesh.instanceMatrix.array as Float32Array, dcol = this.mesh.instanceColor ? this.mesh.instanceColor.array as Float32Array : null;
    let m = 0;
    for (let k = 0; k < count; k++) {
      const c = active[k];
      if (c < 0) continue;
      const a = this.start[c], b = this.start[c + 1];
      dm.set(this.mat.subarray(a * 16, b * 16), m * 16);
      if (dcol && this.col) dcol.set(this.col.subarray(a * 3, b * 3), m * 3);
      m += b - a;
    }
    this.mesh.count = m;
    this.mesh.instanceMatrix.needsUpdate = true;
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true;
  }
}

/** 청크 둘레 PAD칸을 덧댄 판정 격자(필드 격자에서 복사) */
function padGrid(def: FieldDef, c: FieldChunkDef): MapGrid {
  const w = G.chunk + PAD * 2, x0 = c.x0 - PAD, z0 = c.z0 - PAD;
  const cells = new Uint8Array(w * w);
  const fg = def.grid;
  for (let z = 0; z < w; z++) for (let x = 0; x < w; x++) {
    const gx = x0 + x - fg.originX, gz = z0 + z - fg.originZ;
    cells[z * w + x] = gx < 0 || gz < 0 || gx >= fg.width || gz >= fg.height ? Cell.Void : fg.cells[gz * fg.width + gx];
  }
  return { originX: x0, originZ: z0, width: w, height: w, cell: 1, cells, gateCells: new Uint8Array(w * w), lavaCells: new Uint8Array(w * w) };
}
/** 필드 격자 둘레 margin칸 덧댐(가장자리 근접도·물가 거리 질감 공용) */
function marginGrid(g: MapGrid, margin: number): MapGrid {
  const w = g.width + margin * 2, h = g.height + margin * 2;
  const cells = new Uint8Array(w * h);
  for (let z = 0; z < g.height; z++) for (let x = 0; x < g.width; x++) cells[(z + margin) * w + x + margin] = g.cells[z * g.width + x];
  return { originX: g.originX - margin, originZ: g.originZ - margin, width: w, height: h, cell: 1, cells, gateCells: new Uint8Array(w * h), lavaCells: new Uint8Array(w * h) };
}

/** 청크 하나의 지형 메시(바닥·절벽·수면)와 종류별 인스턴스 목록 */
interface ChunkParts {
  readonly group: THREE.Group;
  readonly inst: Map<string, Inst[]>;
  readonly tufts: Emitter[];
  readonly flowers: Emitter[];
  readonly flames: Emitter[];
}

export class FieldRender {
  /** 필드 지형(필드 장면에서만 보인다) */
  readonly group = new THREE.Group();
  /** 표식(마을 필드 문·필드 자리·균열 통로/상자): 장면에 따라 보이기만 바꾼다 */
  readonly markers = new THREE.Group();
  readonly def: FieldDef = fieldZone1();
  /** 분위기(바닥 텍스처·빛)를 따르는 스테이지 지도: 지역1 A(절벽 테라스 유적) */
  readonly moodMap: MapDef = getR1StageMaps().a;
  private readonly chunkGroups: (THREE.Group | null)[] = new Array(CHUNK_COUNT).fill(null);
  private readonly batches: (ChunkBatch | CardBatch)[] = [];
  private started: Promise<void> | null = null;
  private built = false;
  private lastSerial = -1;
  private readonly owned: { dispose(): void }[] = [];
  // 표식
  private readonly door: THREE.Group;
  private readonly stone: THREE.Group;
  private readonly riftGate: THREE.Group;
  private readonly hiddenMark: THREE.Mesh;
  private readonly exitMark: THREE.Group;
  private readonly beacon: THREE.Mesh;
  private readonly riftPortal: THREE.Group;
  private readonly chest: THREE.Group;
  private readonly riftRingMat: THREE.MeshBasicMaterial;
  /** 귀환석 결정·바닥 고리 재질: 영웅이 가까울수록 투명해진다(전투 시야를 가리지 않는다) */
  private readonly stoneCrystalMat: THREE.MeshStandardMaterial;
  private readonly stoneRingMat: THREE.MeshBasicMaterial;
  private readonly beaconMat: THREE.MeshBasicMaterial;
  /** 레인 M: 이번 프레임 활성 다른 플레이어 수(그림은 peers.ts) */
  peersSeen = 0;
  /** 대균열(C12) 연출: 입장 소용돌이·수호자 룬·돌파 기둥·층 분위기(grift.ts) */
  readonly grift: GriftFx;
  /** 대균열이 열린 계정의 필드 균열 입구 안쪽 심홍 고리(일반 균열 보라 고리와 반대로 돈다) */
  private readonly griftGateRing: THREE.Mesh;
  private readonly griftGateMat: THREE.MeshBasicMaterial;
  /** 균열 층 통로(고리·바닥 고리·빛기둥) 재질: 대균열이면 심홍·청금으로 바꾼다 */
  private readonly portalMats: THREE.MeshBasicMaterial[] = [];
  private portalTier = -1;
  private camera: THREE.Camera | null = null;
  private readonly tmpColor = new THREE.Color();

  constructor(private readonly quality: QualityLevel) {
    this.group.name = 'field-env';
    this.group.visible = false;
    this.markers.name = 'field-markers';
    const ring = (r: number, color: number, opacity: number, inner = 0.72): THREE.Mesh => {
      const m = new THREE.Mesh(new THREE.RingGeometry(r * inner, r, 40), new THREE.MeshBasicMaterial({ color, transparent: true, opacity, depthWrite: false, side: THREE.DoubleSide }));
      m.rotation.x = -Math.PI / 2; m.position.y = 0.05;
      this.owned.push(m.geometry, m.material as THREE.Material);
      return m;
    };
    const pillar = (h: number, r: number, color: number, opacity: number): THREE.Mesh => {
      const m = new THREE.Mesh(new THREE.CylinderGeometry(r, r * 1.2, h, 20, 1, true), new THREE.MeshBasicMaterial({ color, transparent: true, opacity, depthWrite: false, side: THREE.DoubleSide, blending: THREE.AdditiveBlending }));
      m.position.y = h / 2;
      this.owned.push(m.geometry, m.material as THREE.Material);
      return m;
    };
    // 마을① 필드 문: 청록 소용돌이 판 + 빛기둥 + 이름표
    this.door = new THREE.Group();
    this.door.add(ring(2.2, 0x6fe8c8, 0.75), pillar(3.2, 1.4, 0x58d8b0, 0.16));
    // 이름표는 시설 이름표 크기(3.4m) — 문 곁에 서면 카메라 위쪽 HUD를 가리지 않게 낮게 둔다
    const dl = labelSprite('필드 · ARPG', '#7FF0D0', 3.4); dl.position.y = 3.6; this.door.add(dl);
    this.door.position.set((FIELD_DOOR.x0 + FIELD_DOOR.x1) / 2, 0, (FIELD_DOOR.z0 + FIELD_DOOR.z1) / 2);
    // 귀환석: 빛나는 결정 + 바닥 고리
    this.stone = new THREE.Group();
    // 불투명 + 높은 발광이면 면 구분이 사라져 하늘색 판 하나로 보이고 사냥터 한가운데서 몹·영웅을 가렸다(2026-10-29 PC GPU 사냥 캡처).
    // 면을 살리고(flatShading) 반투명·깊이 쓰기 없음으로 두며, 영웅이 가까우면 update가 더 투명하게 한다(stoneFade).
    this.stoneCrystalMat = new THREE.MeshStandardMaterial({ color: 0x9fe8ff, emissive: 0x3fb8ff, emissiveIntensity: 0.7, roughness: 0.3, flatShading: true, transparent: true, opacity: STONE_CRYSTAL_OPACITY.far, depthWrite: false });
    const crystal = new THREE.Mesh(new THREE.OctahedronGeometry(0.75, 0), this.stoneCrystalMat);
    crystal.position.y = 1.6; crystal.scale.set(0.8, 1.6, 0.8);
    this.owned.push(crystal.geometry, crystal.material as THREE.Material);
    const plinth = new THREE.Mesh(new THREE.CylinderGeometry(0.55, 0.7, 0.6, 8), new THREE.MeshStandardMaterial({ color: 0x8a8476, roughness: 1 }));
    plinth.position.y = 0.3;
    this.owned.push(plinth.geometry, plinth.material as THREE.Material);
    // 바닥 고리: 띠를 가늘게(안쪽 0.86) — 두꺼운 반투명 띠가 전투 바닥을 덮는 초승달처럼 보였다
    const stoneRing = ring(2.4, 0x7fd4ff, STONE_RING_OPACITY.far, 0.86);
    this.stoneRingMat = stoneRing.material as THREE.MeshBasicMaterial;
    this.stone.add(crystal, plinth, stoneRing);
    const sl = labelSprite('귀환석', '#9FE0FF', 3.4); sl.position.y = 3.6; this.stone.add(sl);
    // 균열 입구: 보랏빛 고리(조각이 모이면 밝아진다)
    this.riftGate = new THREE.Group();
    this.riftRingMat = new THREE.MeshBasicMaterial({ color: 0xb070ff, transparent: true, opacity: 0.5, depthWrite: false, side: THREE.DoubleSide, blending: THREE.AdditiveBlending });
    const rr = new THREE.Mesh(new THREE.TorusGeometry(1.7, 0.18, 10, 40), this.riftRingMat);
    rr.position.y = 2.1;
    this.owned.push(rr.geometry, this.riftRingMat);
    this.riftGate.add(rr, ring(2.0, 0x9a5cff, 0.5));
    const rl = labelSprite('균열', '#C9A0FF', 3.2); rl.position.y = 4.4; this.riftGate.add(rl);
    // 숨은 던전 입구(찾으면 보인다): 어두운 구멍 고리
    this.hiddenMark = ring(1.8, 0x203018, 0.85);
    // 남쪽 출구
    this.exitMark = new THREE.Group();
    this.exitMark.add(ring(2.4, 0x4fb6d0, 0.5));
    const el = labelSprite('마을로', '#4FB6D0', 3.4, 'exit'); el.position.y = 3.2; this.exitMark.add(el);
    this.exitMark.position.set((this.def.exit.x0 + this.def.exit.x1) / 2, 0, this.def.exit.z1 - 1.5);
    // 이벤트 빛기둥(엘리트 빨강·보물 고블린 금빛)
    this.beaconMat = new THREE.MeshBasicMaterial({ color: 0xff4040, transparent: true, opacity: 0.35, depthWrite: false, side: THREE.DoubleSide, blending: THREE.AdditiveBlending });
    this.beacon = new THREE.Mesh(new THREE.CylinderGeometry(0.9, 1.4, 22, 16, 1, true), this.beaconMat);
    this.owned.push(this.beacon.geometry, this.beaconMat);
    // 균열: 층 통로·출구(같은 고리)와 보상 상자
    this.riftPortal = new THREE.Group();
    const pr = new THREE.Mesh(new THREE.TorusGeometry(1.5, 0.2, 10, 40), new THREE.MeshBasicMaterial({ color: 0xd090ff, transparent: true, opacity: 0.85, blending: THREE.AdditiveBlending, depthWrite: false }));
    pr.position.y = 1.8;
    this.owned.push(pr.geometry, pr.material as THREE.Material);
    this.riftPortal.add(pr, ring(1.9, 0xb070ff, 0.6), pillar(6, 1.2, 0xa060ff, 0.18));
    for (const o of this.riftPortal.children) this.portalMats.push((o as THREE.Mesh).material as THREE.MeshBasicMaterial);
    this.griftGateMat = new THREE.MeshBasicMaterial({ color: GRIFT_COLOR.crimson, transparent: true, opacity: 0.6, depthWrite: false, side: THREE.DoubleSide, blending: THREE.AdditiveBlending });
    this.griftGateRing = new THREE.Mesh(new THREE.TorusGeometry(1.15, 0.09, 8, 40), this.griftGateMat);
    this.griftGateRing.position.y = 2.1;
    this.owned.push(this.griftGateRing.geometry, this.griftGateMat);
    this.riftGate.add(this.griftGateRing);
    this.chest = new THREE.Group();
    const box = new THREE.Mesh(new THREE.BoxGeometry(1.4, 0.9, 0.9), new THREE.MeshStandardMaterial({ color: 0x8a5a26, roughness: 0.7 }));
    box.position.y = 0.45;
    const lid = new THREE.Mesh(new THREE.BoxGeometry(1.5, 0.3, 1.0), new THREE.MeshStandardMaterial({ color: 0xe8b84a, emissive: 0x6a4a10, roughness: 0.4, metalness: 0.6 }));
    lid.position.y = 1.0;
    this.owned.push(box.geometry, box.material as THREE.Material, lid.geometry, lid.material as THREE.Material);
    this.chest.add(box, lid, pillar(5, 0.9, 0xffd060, 0.25));
    const cl = labelSprite('보상 상자', '#FFD060', 3.6); cl.position.y = 2.6; this.chest.add(cl);
    const rs = this.def.returnStone, rp = this.def.riftPortal, hd = this.def.hidden;
    this.stone.position.set(rs.x, 0, rs.z);
    this.riftGate.position.set(rp.x, 0, rp.z);
    this.hiddenMark.position.set(hd.x, 0.05, hd.z);
    this.markers.add(this.door, this.stone, this.riftGate, this.hiddenMark, this.exitMark, this.beacon, this.riftPortal, this.chest);
    for (const o of this.markers.children) o.visible = false;
    this.grift = new GriftFx(quality);
    this.markers.add(this.grift.group);
  }

  /** 렌더 묶음 연결: 장면 안개(대균열 층 분위기)·카메라(입자 빌보드)·액션 카메라 디렉터 컷 */
  bindFx(fog: THREE.Fog | null, camera: THREE.Camera, cue: (id: ShotId, x: number, z: number) => boolean): void {
    this.grift.bindFog(fog);
    this.camera = camera;
    this.grift.onCue = cue;
  }

  /** 게임 이벤트(렌더 묶음 onEvent가 넘긴다): 대균열 입장·수호자·결과 */
  onEvent(e: GameEvent, view: GameView): void { this.grift.onEvent(e, view); }

  /** 이 지도가 필드 구역 지도인지 */
  owns(map: MapDef): boolean { return map === this.def.map; }
  /** 처음 보일 3×3이 만들어졌는지(입장 대기 화면이 기다린다) */
  ready(): boolean { return this.built; }

  /** 자산 읽기 + 25청크 생성(한 청크씩 양보하며). 여러 번 불러도 한 번 */
  start(firstChunks?: ArrayLike<number>): Promise<void> {
    this.started ??= this.build(firstChunks);
    return this.started;
  }

  private async build(first?: ArrayLike<number>): Promise<void> {
    const def = this.def, mood = moodOf(this.moodMap), haze = new THREE.Color(mood.haze);
    const themes: ChunkTheme[] = ['a', 'b', 'm', 'f'];
    const used = new Set<SetName>(['edge_stone', 'edge_stone_b', 'edge_root', 'edge_palisade', 'edge_quay', 'tower', 'bonfire', 'root_knot', 'prop_rubble', 'prop_rock', 'prop_crates', 'prop_mushrooms']);
    const [texs, pano, atlas, kitPillar, kitRubble] = await Promise.all([
      Promise.all(themes.map((t) => track(loadTexture(FLOOR_TEX[t])))),
      track(loadTexture('r1/tex/bg_r1_panorama.webp', true)),
      track(loadSetAtlas('r1')),
      track(loadKit('pillar', null)), track(loadKit('rubble', null)),
    ]);
    const assets = new Map<SetName, SetAsset>();
    await Promise.all([...used].map(async (n) => { const a = await track(loadSet(n)); if (a) assets.set(n, a); }));
    // 원경
    const bd = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), backdropMaterial(pano, haze, 2.5, 0.72, 90));
    bd.frustumCulled = false; bd.renderOrder = -1000;
    this.group.add(bd); this.owned.push(bd.geometry, bd.material as THREE.Material);
    // 공용 질감: 가장자리 근접도·물가 거리(필드 격자 + 덧댐)
    const mg = marginGrid(def.grid, 8);
    const prox: ProximityTex = { tex: dataTexture(edgeProximity(mg, 3), mg.width, mg.height), originX: mg.originX, originZ: mg.originZ, width: mg.width, height: mg.height };
    const shore: ProximityTex = { tex: dataTexture(shoreDistance(mg, 8), mg.width, mg.height), originX: mg.originX, originZ: mg.originZ, width: mg.width, height: mg.height };
    this.owned.push(prox.tex, shore.tex);
    const floorMat = new Map<ChunkTheme, THREE.Material>(), cliffMat = new Map<string, THREE.Material>();
    themes.forEach((t, i) => { const m = stageFloorMaterial(texs[i] ?? texs[0], prox, t); floorMat.set(t, m); this.owned.push(m); });
    for (const t of themes) { const m = stageCliffMaterial(t, haze, -CLIFF_DEPTH); cliffMat.set(t, m); this.owned.push(m); }
    const waterMat = stageWaterMaterial(shore, haze);
    this.owned.push(waterMat);
    // 청크: 처음 보일 청크부터
    const order: number[] = [];
    if (first) for (let i = 0; i < first.length; i++) if (first[i] >= 0) order.push(first[i]);
    for (let i = 0; i < CHUNK_COUNT; i++) if (!order.includes(i)) order.push(i);
    const parts: (ChunkParts | null)[] = new Array(CHUNK_COUNT).fill(null);
    for (const ci of order) {
      parts[ci] = this.buildChunk(def.chunks[ci], floorMat.get(def.chunks[ci].theme)!, cliffMat.get(def.chunks[ci].theme)!, waterMat, assets, kitPillar, kitRubble);
      this.chunkGroups[ci] = parts[ci]!.group;
      this.group.add(parts[ci]!.group);
      parts[ci]!.group.visible = false;
      await new Promise((r) => setTimeout(r, 0));
    }
    // 종류별 인스턴싱
    const kinds = new Set<string>();
    for (const p of parts) if (p) for (const k of p.inst.keys()) kinds.add(k);
    const matCache = new Map<string, THREE.Material>();
    for (const k of kinds) {
      const per = parts.map((p) => p?.inst.get(k) ?? []);
      if (k === 'kit:pillar' || k === 'kit:rubble') {
        const kit: KitAsset | null = k === 'kit:pillar' ? kitPillar : kitRubble;
        if (!kit) continue;
        const m = kitMaterial(kit, k === 'kit:pillar' ? 'pillar' : 'rubble', texs[0], false, null);
        this.owned.push(m);
        this.addBatch(new ChunkBatch(kit.geometry, m, per, true, 'field-' + k, true));
        continue;
      }
      const a = assets.get(k as SetName);
      if (!a) continue;
      const cap = SET_CAP[k as SetName] ?? STONE_CAP;
      const clip = k.startsWith('edge_') || k.startsWith('prop_') || k === 'bonfire';
      const mk = (clip ? 'c' : 'n') + cap.join(',');
      let m = matCache.get(mk);
      if (!m) { m = setMaterial(atlas, { clip, cap, emissive: true, glow: 1.5 }); matCache.set(mk, m); this.owned.push(m); }
      this.addBatch(new ChunkBatch(a.geometry, m, per, !clip, 'field-' + k, true));
    }
    for (const [kind, pick] of [['tuft', (p: ChunkParts) => p.tufts], ['flower', (p: ChunkParts) => p.flowers]] as const) {
      const all: Emitter[] = [], counts: number[] = [];
      for (const p of parts) { const l = p ? pick(p) : []; counts.push(l.length); all.push(...l); }
      const mesh = cardField(all, kind, false);
      if (mesh) { this.group.add(mesh); this.owned.push(mesh.geometry, mesh.material as THREE.Material); this.batches.push(new CardBatch(mesh, counts)); }
    }
    {
      const all: Emitter[] = [], counts: number[] = [];
      for (const p of parts) { const l = p ? p.flames : []; counts.push(l.length); all.push(...l); }
      const mesh = flameField(all);
      if (mesh) { this.group.add(mesh); this.owned.push(mesh.geometry, mesh.material as THREE.Material); this.batches.push(new CardBatch(mesh, counts)); }
    }
    this.built = true;
    this.lastSerial = -1;
  }
  private addBatch(b: ChunkBatch): void { this.group.add(b.mesh); this.owned.push(b.mesh.geometry); this.batches.push(b); }

  private buildChunk(c: FieldChunkDef, floorMat: THREE.Material, cliffMat: THREE.Material, waterMat: THREE.Material, assets: Map<SetName, SetAsset>, kp: KitAsset | null, kr: KitAsset | null): ChunkParts {
    const def = this.def, grid = padGrid(def, c);
    const group = new THREE.Group();
    group.name = 'field-chunk-' + c.index;
    const x0 = c.x0, z0 = c.z0, x1 = c.x0 + G.chunk, z1 = c.z0 + G.chunk;
    const inside = (x: number, z: number): boolean => x >= x0 && x < x1 && z >= z0 && z < z1;
    const field = buildField(grid, 4242);
    const contour = contourOf(field, grid);
    // 바닥 윗면: 무게중심이 청크 안인 삼각형 + 청크 안으로 자른 깊은 안쪽 사각형
    const pos: number[] = [];
    for (let i = 0; i < contour.band.length; i += 6) {
      const b = contour.band;
      const cx = (b[i] + b[i + 2] + b[i + 4]) / 3, cz = (b[i + 1] + b[i + 3] + b[i + 5]) / 3;
      if (!inside(cx, cz)) continue;
      pos.push(b[i], 0, b[i + 1], b[i + 2], 0, b[i + 3], b[i + 4], 0, b[i + 5]);
    }
    for (const r of contour.interior) {
      const ax = Math.max(r.x0, x0), az = Math.max(r.z0, z0), bx = Math.min(r.x1, x1), bz = Math.min(r.z1, z1);
      if (ax >= bx || az >= bz) continue;
      pos.push(ax, 0, az, ax, 0, bz, bx, 0, bz, ax, 0, az, bx, 0, bz, bx, 0, az);
    }
    const arr = new Float32Array(pos);
    for (let t = 0; t < arr.length; t += 9) {
      const ax = arr[t], az = arr[t + 2], bx = arr[t + 3], bz = arr[t + 5], cx = arr[t + 6], cz = arr[t + 8];
      if ((bz - az) * (cx - ax) - (bx - ax) * (cz - az) < 0) for (let k = 0; k < 3; k++) { const o = t + 3 + k; const p = arr[o]; arr[o] = arr[o + 3]; arr[o + 3] = p; }
    }
    const fg = new THREE.BufferGeometry();
    fg.setAttribute('position', new THREE.BufferAttribute(arr, 3));
    fg.setAttribute('normal', new THREE.BufferAttribute(new Float32Array(arr.length).map((_, i) => (i % 3 === 1 ? 1 : 0)), 3));
    const floor = new THREE.Mesh(fg, floorMat);
    floor.receiveShadow = true; floor.name = 'field-floor';
    group.add(floor); this.owned.push(fg);
    // 절벽(수로 청크는 얕은 둑) — 청크 안 표본만, 밖으로 나가면 띠를 끊는다
    const water = c.theme === 'b';
    const depth = water ? -WATER_Y + 0.1 : CLIFF_DEPTH;
    const cliffPos: number[] = [], cliffIdx: number[] = [];
    const rows = 5;
    const strip = (chain: Chain): void => {
      const dense = resample(chain, 1.0);
      let prev = -1;
      for (const s of dense) {
        if (!inside(s.x - s.nx * 0.3, s.z - s.nz * 0.3) && !inside(s.x, s.z)) { prev = -1; continue; }
        const base = cliffPos.length / 3;
        for (let k = 0; k <= rows; k++) {
          const t = k / rows, y = k === 0 ? 0.02 : -depth * Math.pow(t, 1.3);
          let bulge = 0;
          if (depth > 3 && k > 0) bulge = (0.22 + (valueNoise(s.x * 0.7 + k * 3.1, s.z * 0.7 - k * 1.7, 31) - 0.42) * (c.theme === 'f' ? 1.9 : 1.15)) * Math.min(1, t * 2.2);
          cliffPos.push(s.x + s.nx * bulge, y, s.z + s.nz * bulge);
        }
        if (prev >= 0) for (let k = 0; k < rows; k++) { const a = prev + k, b = prev + k + 1, cc = base + k, d = base + k + 1; cliffIdx.push(a, cc, b, b, cc, d); }
        prev = base;
      }
    };
    const rnd = mulberry32(9100 + c.index * 31);
    const inst = new Map<string, Inst[]>();
    const push = (k: string, i: Inst): void => { let a = inst.get(k); if (!a) inst.set(k, (a = [])); a.push(i); };
    const tufts: Emitter[] = [], flowers: Emitter[] = [], flames: Emitter[] = [];
    const tint = (): number => { const t = rnd(); return c.theme === 'm' || c.theme === 'f' ? (t < 0.3 ? 0xe8e0d0 : t < 0.6 ? 0xffffff : 0xf4ecdc) : (t < 0.3 ? 0xcfe3bd : t < 0.5 ? 0xdcd5c9 : 0xffffff); };
    const density = DENSITY[this.quality];
    const spacing = 2.4;
    const style = c.edgeStyle;
    const modules = STYLE_MODULES[style] ?? STYLE_MODULES.cliff;
    for (const chain of contour.chains) {
      strip(chain);
      for (const s of resample(chain, spacing)) {
        if (!inside(s.x, s.z)) continue;
        const yaw = Math.atan2(s.nx, s.nz);
        if (!(style === 'roots' && rnd() < 0.55)) {
          const name = modules[Math.floor(rnd() * modules.length)];
          const a = assets.get(name);
          if (a) {
            const lenK = (spacing / Math.max(0.5, a.w)) * (1 + rnd() * 0.12), sc = (0.92 + rnd() * 0.2) * (style === 'roots' ? 0.8 : 1);
            push(name, { x: s.x - s.nx * 0.12, y: 0, z: s.z - s.nz * 0.12, rot: yaw + (rnd() - 0.5) * 0.12, sx: lenK, sy: sc, sz: sc, tint: tint(), cut: 1 });
          }
        }
        if (style !== 'palisade' || rnd() < 0.3) {
          const tn = Math.round((c.theme === 'm' ? 1.2 : 2.6) * density + rnd());
          for (let k = 0; k < tn; k++) {
            const dd = 0.5 + rnd() * 1.4, u = (rnd() - 0.5) * spacing;
            (rnd() < (c.theme === 'f' ? 0.4 : 0.3) ? flowers : tufts).push({ x: s.x - s.nx * dd + s.tx * u, y: 0, z: s.z - s.nz * dd + s.tz * u, s: 0.45 + rnd() * 0.4, v: rnd() });
          }
        }
        if (rnd() < 0.2 * density) {
          const list = SCATTER[c.theme], sc = list[Math.floor(rnd() * list.length)], pa = assets.get(sc.kind);
          if (pa) {
            const dd = 1.0 + rnd() * 1.6, px = s.x - s.nx * dd + s.tx * (rnd() - 0.5) * spacing, pz = s.z - s.nz * dd + s.tz * (rnd() - 0.5) * spacing;
            const h = sc.h[0] + rnd() * (sc.h[1] - sc.h[0]), k2 = h / pa.h;
            push(sc.kind, { x: px, y: 0, z: pz, rot: rnd() * Math.PI * 2, sx: k2, sy: k2, sz: k2, tint: tint(), cut: 1 });
          }
        }
      }
    }
    if (cliffIdx.length > 0) {
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.Float32BufferAttribute(cliffPos, 3));
      g.setIndex(cliffIdx);
      g.computeVertexNormals();
      const cliff = new THREE.Mesh(g, cliffMat);
      cliff.name = 'field-cliff';
      group.add(cliff); this.owned.push(g);
    }
    if (water) {
      const g = new THREE.PlaneGeometry(G.chunk, G.chunk);
      g.rotateX(-Math.PI / 2); g.translate(x0 + G.chunk / 2, WATER_Y, z0 + G.chunk / 2);
      const wm = new THREE.Mesh(g, waterMat);
      wm.receiveShadow = true; wm.name = 'field-water';
      group.add(wm); this.owned.push(g);
    }
    // 랜드마크(판정 사각형 = 모양 자리): 기둥·잔해는 유적 키트, 망루·모닥불·뿌리 매듭은 세트피스
    for (const l of c.landmarks) {
      const w = l.rect.x1 - l.rect.x0, d = l.rect.z1 - l.rect.z0, cx = (l.rect.x0 + l.rect.x1) / 2, cz = (l.rect.z0 + l.rect.z1) / 2;
      const rot = rnd() * Math.PI * 2;
      if (l.kind === 'pillar' && kp) push('kit:pillar', { x: cx, y: 0, z: cz, rot, sx: 1.55 / kp.w, sy: l.height / kp.h, sz: 1.55 / kp.d, tint: 0xffffff, cut: 2 });
      else if (l.kind === 'rubble' && kr) push('kit:rubble', { x: cx, y: 0, z: cz, rot, sx: (w * 0.95) / kr.w, sy: Math.max(1.25, l.height) / kr.h, sz: (d * 0.9) / kr.d, tint: 0xffffff, cut: 2 });
      else if (l.kind === 'tower' || l.kind === 'bonfire' || l.kind === 'root') {
        const n: SetName = l.kind === 'root' ? 'root_knot' : l.kind;
        const a = assets.get(n);
        if (!a) continue;
        const sx = w / a.w, sz = d / a.d, sy = l.kind === 'root' ? l.height / a.h : Math.min(sx, sz);
        push(n, { x: cx, y: 0, z: cz, rot, sx, sy, sz, tint: 0xffffff, cut: 2 });
        if (l.kind === 'bonfire') flames.push({ x: cx, y: 1.15 * sy, z: cz, s: 1.25, v: rnd() });
        if (l.kind === 'tower') flames.push({ x: cx, y: 6.55 * sy, z: cz, s: 1.0, v: rnd() });
      }
    }
    return { group, inst, tufts, flowers, flames };
  }

  setVisible(on: boolean): void { this.group.visible = on; }

  /** 매 프레임: 활성 청크 보이기(바뀔 때만 인스턴스 모음), 표식 위치·보이기 */
  update(view: GameView, nowMs: number): void {
    const fv = view.field, sc = view.flow.scene;
    const inField = sc === Scene.Field && !!fv && this.owns(view.map);
    if (inField && this.built && fv && fv.chunkSerial !== this.lastSerial) {
      this.lastSerial = fv.chunkSerial;
      for (let i = 0; i < CHUNK_COUNT; i++) { const g = this.chunkGroups[i]; if (g) g.visible = false; }
      for (let k = 0; k < fv.activeCount; k++) { const g = this.chunkGroups[fv.activeChunks[k]]; if (g) g.visible = true; }
      for (let i = 0; i < this.batches.length; i++) this.batches[i].fill(fv.activeChunks, fv.activeCount);
    }
    // 표식
    const t = nowMs / 1000;
    this.door.visible = sc === Scene.Village && view.flow.town === 1 && !!fv && fv.unlocked;
    if (this.door.visible) this.door.children[0].rotation.z = t * 0.8;
    this.stone.visible = inField; this.riftGate.visible = inField; this.exitMark.visible = inField;
    this.hiddenMark.visible = inField && !!fv && fv.hiddenFound;
    if (inField && fv) {
      this.stone.children[0].rotation.y = t * 0.9;
      const k = stoneFade(Math.hypot(view.hero.x - this.stone.position.x, view.hero.z - this.stone.position.z));
      this.stoneCrystalMat.opacity = STONE_CRYSTAL_OPACITY.near + (STONE_CRYSTAL_OPACITY.far - STONE_CRYSTAL_OPACITY.near) * k;
      this.stoneRingMat.opacity = STONE_RING_OPACITY.near + (STONE_RING_OPACITY.far - STONE_RING_OPACITY.near) * k;
      const ready = fv.shards >= 5;
      this.riftRingMat.opacity = ready ? 0.85 + Math.sin(t * 4) * 0.1 : 0.35;
      this.riftGate.children[0].rotation.y = t * (ready ? 1.6 : 0.4);
      // 대균열이 열렸으면(상한 1단계 이상) 안쪽 심홍 고리가 반대로 돌며 청금빛으로 숨쉰다
      const gOn = fv.rift.cap > 0;
      this.griftGateRing.visible = gOn;
      if (gOn) {
        this.griftGateRing.rotation.y = -t * (ready ? 2.2 : 0.7);
        this.griftGateRing.rotation.x = Math.sin(t * 0.9) * 0.25;
        const k = 0.5 + 0.5 * Math.sin(t * 2.4);
        this.griftGateMat.color.setHex(GRIFT_COLOR.crimson).lerp(this.tmpColor.setHex(GRIFT_COLOR.lapis), k * 0.45);
        this.griftGateMat.opacity = ready ? 0.75 : 0.45;
      }
    }
    const ev = fv?.event;
    this.beacon.visible = inField && !!ev && ev.kind !== FieldEventKind.None;
    if (this.beacon.visible && ev) {
      this.beacon.position.set(ev.x, 11, ev.z);
      this.beaconMat.color.setHex(ev.kind === FieldEventKind.Elite ? 0xff4040 : 0xffc840);
      this.beaconMat.opacity = 0.28 + Math.sin(t * 3) * 0.08;
    }
    const r = fv?.rift, inRift = sc === Scene.Rift && !!r;
    // 대균열 층 통로: 고리 심홍 · 바닥 고리·빛기둥 청금(일반 균열은 보라 그대로)
    const pt = inRift && r && r.tier > 0 ? 1 : 0;
    if (pt !== this.portalTier) {
      this.portalTier = pt;
      const [torus, floor, beam] = this.portalMats;
      torus.color.setHex(pt ? GRIFT_COLOR.crimson : 0xd090ff);
      floor.color.setHex(pt ? GRIFT_COLOR.lapis : 0xb070ff);
      beam.color.setHex(pt ? 0x2f46b8 : 0xa060ff); // 청금 기둥은 보라보다 진해 화면을 물들였다(캡처): 한 단계 어둡게
    }
    this.riftPortal.visible = inRift && !!r && r.portalActive;
    if (this.riftPortal.visible && r) { this.riftPortal.position.set(r.portalX, 0, r.portalZ); this.riftPortal.children[0].rotation.y = t * 1.5; }
    this.chest.visible = inRift && !!r && r.chestActive && r.phase === RiftPhase.Chest;
    if (this.chest.visible && r) this.chest.position.set(r.chestX, 0, r.chestZ);
    // 레인 M 연결점: 다른 플레이어(같은 필드 채널). 그림은 render/field/peers.ts(레인 M)가 이 배열을 읽어 그린다
    const peers = fv?.net?.peers;
    let n = 0;
    if (peers) for (let i = 0; i < peers.length; i++) if (peers[i].active) n++;
    this.peersSeen = n;
    this.grift.update(view, nowMs, this.camera);
  }

  dispose(): void {
    this.grift.dispose();
    for (const o of this.owned) o.dispose();
    this.owned.length = 0;
  }
}
